"use client";

import { Box, Container, Stack } from "@mui/material";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { TurnstileWidget, useTurnstile } from "@/hooks/useTurnstile";
import type { StoreData } from "@/server-fns/booking/query";
import type { BookingIntent } from "@/types/booking-intent";

import { AICommandBar } from "./AICommandBar";
import type { GuestContactValues } from "./AIContactForm";
import { AIContactForm } from "./AIContactForm";
import { BookingSuccess } from "./BookingSuccess";
import type { GuestBookingPayload } from "./BookingSummary";
import { BookingSummary } from "./BookingSummary";
import type { GuestBookingFormValues } from "./Form";
import { isCardCollectionRequired } from "./Form";
import { PaymentStep } from "./PaymentStep";
import { getResumeSetupIntentClientSecret, readPendingBooking } from "./pendingBookingStorage";
import { StoreInfo } from "./StoreInfo";

type Step = "SEARCH" | "CONTACT" | "SUMMARY" | "PAYMENT" | "SUCCESS";

const stepOrder: Step[] = ["SEARCH", "CONTACT", "SUMMARY", "PAYMENT", "SUCCESS"];

// See Form.tsx's getResumePendingPayload / pendingBookingStorage.ts — same 3DS-redirect resume,
// for the AI flow's independent step machine.
const getResumePendingPayload = (storeData: StoreData): GuestBookingPayload | null => {
    if (!isCardCollectionRequired(storeData)) return null;
    if (!getResumeSetupIntentClientSecret()) return null;
    return readPendingBooking()?.payload ?? null;
};

const variants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 48 : -48,
        opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({
        x: direction > 0 ? -48 : 48,
        opacity: 0,
    }),
};

type Props = {
    storeId: string;
    storeData: StoreData;
    onManualMode: (hasInput: boolean) => void;
};

export const AIView = ({ storeId, storeData, onManualMode }: Props) => {
    const posthog = usePostHog();
    const [pendingPayload, setPendingPayload] = useState<GuestBookingPayload | null>(() =>
        getResumePendingPayload(storeData),
    );
    const [step, setStep] = useState<Step>(() => (pendingPayload ? "PAYMENT" : "SEARCH"));
    const [prevStep, setPrevStep] = useState<Step>(() => (pendingPayload ? "PAYMENT" : "SEARCH"));
    const [confirmedIntent, setConfirmedIntent] = useState<BookingIntent | null>(null);
    const [guestValues, setGuestValues] = useState<GuestContactValues | null>(null);
    const [isAutoConfirmed, setIsAutoConfirmed] = useState(false);
    const { captchaToken, setCaptchaToken } = useTurnstile();
    const requiresCardCollection = isCardCollectionRequired(storeData);

    const direction = stepOrder.indexOf(step) - stepOrder.indexOf(prevStep);

    const goTo = (next: Step) => {
        setPrevStep(step);
        setStep(next);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBookingIntent = (intent: BookingIntent) => {
        if (!intent.date || !intent.time || !intent.adults) return;
        setConfirmedIntent(intent);
        goTo("CONTACT");
    };

    const handleContactComplete = (values: GuestContactValues) => {
        setGuestValues(values);
        posthog.capture("booking_ai_contact_completed");
        goTo("SUMMARY");
    };

    const handleReset = () => {
        setPrevStep(step);
        setStep("SEARCH");
        setConfirmedIntent(null);
        setGuestValues(null);
        setIsAutoConfirmed(false);
        setPendingPayload(null);
    };

    const handleBookingSuccess = (isConfirmed: boolean) => {
        setIsAutoConfirmed(isConfirmed);
        goTo("SUCCESS");
    };

    const renderStep = () => {
        if (step === "SUCCESS") {
            return (
                <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, px: { xs: 0, sm: 3 } }}>
                    <BookingSuccess
                        email={guestValues?.email ?? ""}
                        isAutoConfirmed={isAutoConfirmed}
                        onReturnHome={handleReset}
                    />
                </Container>
            );
        }

        if (step === "PAYMENT" && pendingPayload) {
            return (
                <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, px: { xs: 0, sm: 3 } }}>
                    <PaymentStep
                        payload={pendingPayload}
                        storeData={storeData}
                        captchaToken={captchaToken}
                        onBack={() => goTo("SUMMARY")}
                        onSuccess={handleBookingSuccess}
                    />
                </Container>
            );
        }

        if (step === "SUMMARY" && confirmedIntent && guestValues) {
            const summaryValues: GuestBookingFormValues = {
                adults: confirmedIntent.adults ?? 1,
                kids: confirmedIntent.kids ?? 0,
                highchairs: confirmedIntent.highchairs ?? 0,
                date: dayjs(confirmedIntent.date!),
                time: confirmedIntent.time!,
                areaId: confirmedIntent.areaId,
                areaName: confirmedIntent.areaName ?? undefined,
                fullName: guestValues.fullName,
                phoneNumber: guestValues.phoneNumber,
                email: guestValues.email,
                note: guestValues.note,
            };
            return (
                <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, px: { xs: 0, sm: 3 } }}>
                    <BookingSummary
                        values={summaryValues}
                        storeId={storeId}
                        storeData={storeData}
                        captchaToken={captchaToken}
                        requiresCardCollection={requiresCardCollection}
                        onBack={() => goTo("CONTACT")}
                        onProceedToPayment={(payload) => {
                            setPendingPayload(payload);
                            goTo("PAYMENT");
                        }}
                        onSuccess={handleBookingSuccess}
                    />
                </Container>
            );
        }

        if (step === "CONTACT" && confirmedIntent) {
            return (
                <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, px: { xs: 0, sm: 3 } }}>
                    <TurnstileWidget onTokenChange={setCaptchaToken} />
                    <AIContactForm
                        intent={confirmedIntent}
                        onBack={() => goTo("SEARCH")}
                        onComplete={handleContactComplete}
                        initialValues={guestValues ?? undefined}
                    />
                </Container>
            );
        }

        // SEARCH
        return (
            <Stack
                spacing={{ xs: 8, md: 12 }}
                px={{ xs: 0, md: 8 }}
                maxWidth="md"
                mx="auto"
                alignItems="center"
            >
                <Box sx={{ width: "100%", "& h1": { textAlign: "center" } }}>
                    <StoreInfo data={storeData} variant="banner" wideImage={true} />
                </Box>
                <AICommandBar
                    storeId={storeId}
                    storeTimezone={storeData.timezone}
                    onBookingIntent={handleBookingIntent}
                    onManualMode={onManualMode}
                />
                <Box sx={{ width: "100%", paddingTop: 10 }}>
                    <StoreInfo data={storeData} variant="cards" />
                </Box>
            </Stack>
        );
    };

    return (
        <Box sx={{ overflow: "hidden" }}>
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={step}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeOut" }}
                >
                    {renderStep()}
                </motion.div>
            </AnimatePresence>
        </Box>
    );
};
